package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_business_name_required
import evoshift.composeapp.generated.resources.error_session_expired
import evoshift.composeapp.generated.resources.error_workplace_name_required
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.billing.PlanCheck
import com.evoapps.evoshift.domain.billing.PlanResource
import com.evoapps.evoshift.domain.usecase.CreateWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.billing.CheckPlanLimitUseCase
import com.evoapps.evoshift.domain.usecase.GetOwnerUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class CreateWorkplaceUiState(
    val isCheckingOwner: Boolean = true,
    val hasExistingOwnerProfile: Boolean = false,
    val isLoading: Boolean = false,
    val businessName: String = "",
    val workplaceName: String = "",
    val error: UiText? = null,
    val isCreated: Boolean = false,
    /** Workplaces the owner already has (for the plan limit). */
    val existingWorkplaceCount: Int = 0,
    /** The owner's plan allows no more workplaces: the screen offers the plans (SH-38). */
    val planLimit: PlanCheck.LimitReached? = null
)

sealed interface CreateWorkplaceEvent {
    data class UpdateBusinessName(val name: String) : CreateWorkplaceEvent
    data class UpdateName(val name: String) : CreateWorkplaceEvent
    /** [allDaySlotLabel]: the default availability slot's name in the owner's language (SH-60). */
    data class Submit(val allDaySlotLabel: String) : CreateWorkplaceEvent
    data object ErrorShown : CreateWorkplaceEvent
    data object PlanLimitShown : CreateWorkplaceEvent
}

class WorkplaceCreationViewModel(
    private val createWorkplaceUseCase: CreateWorkplaceUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getOwnerUseCase: GetOwnerUseCase,
    private val checkPlanLimit: CheckPlanLimitUseCase? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(CreateWorkplaceUiState())
    val uiState: StateFlow<CreateWorkplaceUiState> = _uiState.asStateFlow()

    init {
        checkOwnerStatus()
    }

    private fun checkOwnerStatus() {
        viewModelScope.launch {
            _uiState.update { it.copy(isCheckingOwner = true) }
            val user = getCurrentUserUseCase().first()
            if (user != null) {
                val ownerResult = getOwnerUseCase(user.uid)
                val existingOwner = ownerResult.getOrNull()
                _uiState.update { 
                    it.copy(
                        isCheckingOwner = false,
                        hasExistingOwnerProfile = existingOwner != null,
                        existingWorkplaceCount = existingOwner?.workplaceIds?.size ?: 0
                    )
                }
            } else {
                _uiState.update { it.copy(isCheckingOwner = false) }
            }
        }
    }

    fun onEvent(event: CreateWorkplaceEvent) {
        when (event) {
            is CreateWorkplaceEvent.UpdateBusinessName -> {
                _uiState.update { it.copy(businessName = event.name) }
            }
            is CreateWorkplaceEvent.UpdateName -> {
                _uiState.update { it.copy(workplaceName = event.name) }
            }
            is CreateWorkplaceEvent.Submit -> createWorkplace(event.allDaySlotLabel)
            CreateWorkplaceEvent.ErrorShown -> {
                _uiState.update { it.copy(error = null) }
            }
            CreateWorkplaceEvent.PlanLimitShown -> {
                _uiState.update { it.copy(planLimit = null) }
            }
        }
    }

    private fun createWorkplace(allDaySlotLabel: String) {
        val state = _uiState.value
        val workplaceName = state.workplaceName
        val businessName = state.businessName

        if (workplaceName.isBlank()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.error_workplace_name_required)) }
            return
        }
        
        if (!state.hasExistingOwnerProfile && businessName.isBlank()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.error_business_name_required)) }
            return
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }

            // The owner's plan decides how many workplaces there may be (SH-38).
            val check = checkPlanLimit?.invoke(PlanResource.WORKPLACES, state.existingWorkplaceCount)
            if (check is PlanCheck.LimitReached) {
                _uiState.update { it.copy(isLoading = false, planLimit = check) }
                return@launch
            }

            val user = getCurrentUserUseCase().first()

            if (user == null) {
                _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_session_expired)) }
                return@launch
            }

            createWorkplaceUseCase(
                ownerId = user.uid, 
                workplaceName = workplaceName,
                businessName = businessName.takeIf { !state.hasExistingOwnerProfile },
                allDaySlotLabel = allDaySlotLabel
            ).fold(
                onSuccess = {
                    AppTelemetry.log(AnalyticsEvent.WorkplaceCreated)
                    _uiState.update { it.copy(isLoading = false, isCreated = true) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                }
            )
        }
    }
}